/** @jsxImportSource @emotion/react */
import type { FunctionInterpolation, Interpolation } from "@emotion/react";
import { HallowAnalyticsEvent } from "@packages/analytics";
import { Link } from "@packages/framework";
import type { TheWordSectionItem } from "@packages/sdk";
import { differenceInHours } from "date-fns/differenceInHours";

import {
  constantSemanticColors,
  fontWeights,
  numericPercentages,
  radius,
  spacing,
  zIndices,
} from "../../../../../../global/emotion";
import type { CSSColorStrict } from "../../../../../utils/colors/types/CSSColorStrict.types";
import { Button } from "../../../_core/Button/Button.emotion";
import { TrophyIcon } from "../../../icons/Icon/GeneralIcons";
import type { ContentMediumProps } from "../../content/ContentMedium/ContentMedium.emotion";
import { calculateWon } from "../../the-word/calculateWon";
import type { TheWordLargeProps } from "../../the-word/TheWordLarge.emotion";
import type { TheWordMediumProps } from "../../the-word/TheWordMedium.emotion";
import type { BaseGetSectionItemProps } from "./Section.emotion";

const overlayButton: Interpolation = {
  left: numericPercentages[50],
  position: "absolute",
  top: numericPercentages[50],
  transform: "translate(-50%, -50%)",
  zIndex: zIndices[1],
};

const successNumberStyle: FunctionInterpolation<{
  color: TheWordMediumProps["color"];
}> = (props) => ({
  background: constantSemanticColors.onColorHighest,
  borderRadius: radius.full,
  color: props.color,
  display: "inline-flex",
  fontWeight: fontWeights.buttonM,
  height: spacing.l,
  placeContent: "center",
  placeItems: "center",
  width: spacing.l,
});

export type GetSectionItemTheWordPropsProps =
  BaseGetSectionItemProps<TheWordSectionItem>;
export const getSectionItemTheWordProps = ({
  item,
  link,
  t,
  analytics,
}: GetSectionItemTheWordPropsProps): TheWordLargeProps => {
  const gameFinished = item?.reference?.game?.state === "FINISHED";
  const gameUnstarted = item?.reference?.game?.state === "UNSTARTED";

  const actions = link
    ? [
        <Button
          asChild
          key="links"
          variant="white"
          onClick={() =>
            analytics.track({
              event: gameUnstarted
                ? HallowAnalyticsEvent.TappedStartTheWord
                : gameFinished
                  ? HallowAnalyticsEvent.TappedTheWordSummary
                  : HallowAnalyticsEvent.TappedResumeTheWord,
              properties: {
                game_id: item?.reference?.game?.id,
                word: item?.reference?.game?.word,
                word_id: item?.reference?.game?.id,
              },
            })
          }
        >
          <Link href={"/the-word"}>
            {t(
              gameUnstarted
                ? "the_word_play_cta"
                : gameFinished
                  ? "web_review_trivia"
                  : "resume_trivia",
            )}
          </Link>
        </Button>,
      ]
    : null;

  const tags = item?.reference?.game
    ? [
        t("x_playing_plural", {
          x_playing_plural: item.reference.game.player_count,
        }),
        t("ends_in_hours", {
          ends_in_hours: differenceInHours(
            new Date(item.reference.game.ends_at),
            new Date(),
          ),
        }),
      ]
    : null;

  const color = item?.reference?.game?.images?.color_hex as CSSColorStrict;
  const summary = gameFinished ? item?.reference?.game?.summary : null;
  const won = calculateWon({ matrix: summary?.matrix });

  return {
    actions,
    centerActions: actions,
    color,
    description: won
      ? t.rich("the_word_successful_explanation", {
          "0": summary.matrix.length,
          b: (chunks) => (
            <span css={successNumberStyle({ color })}>{chunks}</span>
          ),
        })
      : summary
        ? t("the_word_failed_description")
        : t("the_word_card_description"),
    imgSrc: item?.reference?.game?.images,
    summary,
    tags,
    title: gameFinished ? t("the_word_finished_title") : t("the_word_title"),
    won,
  };
};

export const getSmallSectionItemTheWordProps = ({
  t,
  item,
}: GetSectionItemTheWordPropsProps): ContentMediumProps => {
  const gameFinished = item?.reference?.game?.state === "FINISHED";
  const summary = gameFinished ? item?.reference?.game?.summary : null;
  const won = calculateWon({ matrix: summary?.matrix });
  return {
    title: t("the_word_title"),
    description:
      gameFinished && won
        ? t("the_word_solved_in_x_tries", {
            the_word_solved_in_x_tries: summary?.matrix?.length,
          })
        : gameFinished
          ? t("try_game_again_tomorrow")
          : t("game_resets_in_hours", {
              game_resets_in_hours: differenceInHours(
                new Date(item?.reference?.game?.ends_at),
                new Date(),
              ),
            }),
    metadata: t("games_underline"),
    metadataInlineIcon: <TrophyIcon.Inline />,
    imgSrc: item?.reference?.game?.images,
    asChild: true,
    children: <Link href="/the-word" />,
    imgOverlay: gameFinished ? null : (
      <Button css={overlayButton} variant="secondary">
        {t("button_description_play")}
      </Button>
    ),
  };
};
